import React, { useState, useMemo, useEffect } from 'react';
import { Plus, MapPin, Users } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../store/stableEmpties';
import { useTranslation } from '../../i18n';
import { ACQUISITION_CONSTANTS } from '../../store/slices/acquisitionSlice';
import { MAP_CITIES, findCityById } from '../../data/mapCities';
import { getCityBaseRent, getDepotCityId, OPEN_DEPOT_SUBSIDY_RATE, MAX_INTERNATIONAL_DEPOTS } from '../../data/depotGeo';
import { DEPOT_MANAGERS } from '../../data/depotManagers';
import AvatarWithFallback from '../AvatarWithFallback';

/**
 * NewDepotModal — flux d'ouverture d'un nouveau dépôt (ville + nom + coût).
 * Extrait de DepotTabs (14/06/2026) pour être réutilisé par la carte du
 * réseau (clic sur une ville libre → ouverture directe, `initialCityId`).
 *
 * 05/02/2026 (Q1 retour user prod) — Étape supplémentaire obligatoire :
 * sélection d'un responsable de site (parmi les profils non encore
 * recrutés). Sans manager choisi, l'ouverture est refusée par le slice.
 */
export default function NewDepotModal({ open, onClose, initialCityId = null }) {
  const { t } = useTranslation();
  const depots = useGameStore(s => s.ownedDepots || EMPTY_ARRAY);
  const openNewDepot = useGameStore(s => s.openNewDepot);
  const treasury = useGameStore(s => s.treasury || 0);
  const phase = useGameStore(s => s.phase || 1);
  const company = useGameStore(s => s.company);
  const hiredMgrs = useGameStore(s => s.hiredDepotManagers || EMPTY_OBJECT);
  const [newName, setNewName] = useState('');
  const [selectedCityId, setSelectedCityId] = useState('');
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setError('');
      setNewName('');
      setSelectedCityId(initialCityId || '');
      setSelectedManagerId('');
    }
  }, [open, initialCityId]);

  const grossCost = ACQUISITION_CONSTANTS.NEW_DEPOT_OPEN_COST;
  const subsidy = Math.round(grossCost * OPEN_DEPOT_SUBSIDY_RATE);
  const cost = grossCost - subsidy;
  // Liste des managers dispos (non encore recrutés).
  const availableManagers = useMemo(
    () => DEPOT_MANAGERS.filter(m => !hiredMgrs[m.id]),
    [hiredMgrs],
  );
  const selectedMgr = availableManagers.find(m => m.id === selectedManagerId);
  const managerSigningBonus = selectedMgr?.monthlyCost || 0;
  const totalCost = cost + managerSigningBonus;
  const canAfford = treasury >= totalCost;

  // Villes proposables : débloquées par la phase, pas encore couvertes par
  // un de nos dépôts. Les villes UE n'arrivent qu'en phase 4 (2 slots max).
  const occupiedCityIds = useMemo(
    () => new Set(depots.map(d => getDepotCityId(d, company))),
    [depots, company],
  );
  const intlCount = useMemo(
    () => depots.filter(d => {
      const c = findCityById(getDepotCityId(d, company));
      return c && c.country !== 'FR';
    }).length,
    [depots, company],
  );
  const selectableCities = useMemo(
    () => MAP_CITIES.filter(c => {
      if (c.phase > phase) return false;
      if (occupiedCityIds.has(c.id)) return false;
      if (c.country !== 'FR' && intlCount >= MAX_INTERNATIONAL_DEPOTS) return false;
      return true;
    }),
    [phase, occupiedCityIds, intlCount],
  );
  const selectedCity = findCityById(selectedCityId);

  const handleOpen = () => {
    setError('');
    if (!selectedCityId) { setError(t('depot_tabs_err_no_city')); return; }
    if (!selectedManagerId) { setError('Choisis un responsable de site (obligatoire).'); return; }
    const cityName = selectedCity?.name || '';
    const fallbackName = `${t('depot_tabs_default_name')} ${cityName || depots.length + 1}`;
    const res = openNewDepot(newName || fallbackName, selectedCityId, selectedManagerId);
    if (!res.ok) {
      if (res.reason === 'insufficient_funds') setError(t('depot_tabs_err_insufficient', { amount: (totalCost - treasury).toLocaleString('fr-FR') }));
      else if (res.reason === 'manager_required') setError('Sélectionne un responsable de site avant de continuer.');
      else if (res.reason === 'manager_already_hired') setError('Ce responsable est déjà recruté ailleurs.');
      else if (res.reason === 'max_depots_reached' || res.reason === 'max_opened_depots_reached') setError(t('depot_tabs_err_max'));
      else if (res.reason === 'city_already_covered') setError(t('depot_tabs_err_city_taken'));
      else if (res.reason === 'max_international_depots') setError(t('depot_tabs_err_max_intl'));
      else if (res.reason === 'city_phase_locked') setError(t('depot_tabs_err_phase', { phase: res.requiredPhase }));
      else setError(t('depot_tabs_err_generic'));
      return;
    }
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/75 z-[100] flex items-center justify-center p-4 backdrop-blur-sm" data-testid="new-depot-modal">
      <div className="bg-ink-800 rounded-xl shadow-2xl w-full max-w-md border-2 border-accent/40">
        <div className="px-6 py-4 border-b border-ink-600">
          <h2 className="text-lg font-bold text-foreground">{t('depot_tabs_modal_title')}</h2>
          <p className="text-xs text-slate-400 mt-1">
            {t('depot_tabs_open_cost_label')}: <strong className="font-mono text-amber-300">{cost.toLocaleString('fr-FR')} €</strong>{' '}
            <span className="text-emerald-300">({grossCost.toLocaleString('fr-FR')} € − {subsidy.toLocaleString('fr-FR')} € {t('depot_tabs_subsidy_label')})</span> {t('depot_tabs_open_cost_suffix')}
          </p>
        </div>
        <div className="px-6 py-5 space-y-3">
          <div>
            <span className="text-xs uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {t('depot_tabs_city_label')}
            </span>
            <div className="mt-1.5 grid grid-cols-2 gap-1.5 max-h-44 overflow-y-auto pr-1" data-testid="new-depot-city-list">
              {selectableCities.map(c => {
                const isSel = selectedCityId === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setSelectedCityId(c.id)}
                    className={`px-2.5 py-2 rounded-lg border text-left text-xs transition-all ${
                      isSel ? 'border-accent bg-accent/15 text-foreground' : 'border-ink-600 text-slate-300 hover:border-ink-500'
                    }`}
                    data-testid={`new-depot-city-${c.id}`}
                  >
                    <span className="font-semibold flex items-center gap-1">
                      {c.name}
                      {c.country !== 'FR' && <span className="text-[10px] px-1 rounded bg-purple-500/20 text-purple-300 uppercase">{c.country}</span>}
                    </span>
                    <span className="block text-[10px] text-slate-500 font-mono">
                      {t('depot_tabs_rent_label')} ≈ {Math.round(getCityBaseRent(c.id) * 0.8 * (c.country !== 'FR' ? 1.25 : 1)).toLocaleString('fr-FR')} €/m
                    </span>
                  </button>
                );
              })}
            </div>
            {selectedCity && (
              <p className="text-[10px] text-emerald-300 mt-1.5">
                ✓ {t('depot_tabs_aid_note')}
              </p>
            )}
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-slate-400 font-bold">{t('depot_tabs_name_label')}</span>
            <input
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder={`${t('depot_tabs_default_name')} ${depots.length + 1}`}
              maxLength={32}
              className="mt-1 w-full px-3 py-2 rounded-lg bg-ink-700 border border-ink-600 text-foreground placeholder-slate-500 focus:border-accent focus:outline-none text-sm"
              data-testid="new-depot-name"
            />
          </label>

          {/* 05/02/2026 — Q1 retour user prod : sélection responsable obligatoire */}
          <div>
            <span className="text-xs uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1">
              <Users className="w-3 h-3" /> Responsable de site (obligatoire)
            </span>
            <div className="mt-1.5 grid grid-cols-1 gap-1.5 max-h-44 overflow-y-auto pr-1" data-testid="new-depot-manager-list">
              {availableManagers.length === 0 && (
                <p className="text-[11px] text-amber-300 italic">Aucun responsable disponible. Licencie un manager actuel pour libérer un slot.</p>
              )}
              {availableManagers.map(m => {
                const isSel = selectedManagerId === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setSelectedManagerId(m.id)}
                    className={`px-2.5 py-2 rounded-lg border text-left text-xs transition-all flex items-center gap-2 ${
                      isSel ? 'border-accent bg-accent/15 text-foreground' : 'border-ink-600 text-slate-300 hover:border-ink-500'
                    }`}
                    data-testid={`new-depot-manager-${m.id}`}
                  >
                    <AvatarWithFallback
                      src={m.portrait}
                      alt={m.name}
                      fallback={m.name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2)}
                      className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="font-semibold flex items-center gap-1.5">
                        {m.name}
                        <span className="text-[10px] px-1 rounded bg-ink-700 text-slate-400 uppercase">{m.seniority}</span>
                      </span>
                      <span className="block text-[10px] text-slate-500 font-mono">
                        {m.region} · {m.monthlyCost.toLocaleString('fr-FR')} €/m
                      </span>
                      <span className="block text-[10px] text-amber-200 mt-0.5">★ {m.bonus?.label}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className={`p-3 rounded-lg border text-xs ${canAfford ? 'bg-emerald-950/30 border-emerald-500/40 text-emerald-100' : 'bg-rose-950/30 border-rose-500/40 text-rose-100'}`}>
            {t('depot_tabs_treasury_label')}: <strong className="font-mono">{treasury.toLocaleString('fr-FR')} €</strong>
            {selectedMgr && (
              <span className="block mt-1 text-[10px] text-slate-300">
                Coût total : <span className="font-mono">{cost.toLocaleString('fr-FR')} €</span> implantation + <span className="font-mono">{managerSigningBonus.toLocaleString('fr-FR')} €</span> prime de signature ({selectedMgr.name}) = <strong className="font-mono">{totalCost.toLocaleString('fr-FR')} €</strong>
              </span>
            )}
          </div>
          {error && <div className="p-2 rounded bg-rose-500/15 text-rose-200 text-xs border border-rose-500/30">{error}</div>}
        </div>
        <div className="px-6 py-4 border-t border-ink-600 flex justify-end gap-2 bg-ink-900/40">
          <button onClick={onClose} className="px-3 py-2 rounded-lg bg-ink-600 hover:bg-ink-500 text-slate-200 text-xs" data-testid="new-depot-cancel">
            {t('cancel')}
          </button>
          <button
            onClick={handleOpen}
            disabled={!canAfford || !selectedCityId || !selectedManagerId}
            className="px-5 py-2 rounded-lg bg-accent hover:bg-accent/90 disabled:bg-ink-700 disabled:text-slate-500 disabled:cursor-not-allowed text-white text-sm font-bold flex items-center gap-2"
            data-testid="new-depot-confirm"
          >
            <Plus className="w-4 h-4" /> {t('depot_tabs_open_short')}
          </button>
        </div>
      </div>
    </div>
  );
}
